import {
  ALL_LOCATIONS_ID,
  type SystemAnalysis,
  type SystemDomain,
  type SystemStatus,
} from "./systemAnalysis";

/**
 * The four statuses of the "over time" view: Fully fulfilled, Almost fully,
 * Not at all and Not relevant (does not count towards scores).
 */
export type TrendStatus = "F" | "A" | "N" | "X";

export const TREND_POINTS: Record<Exclude<TrendStatus, "X">, number> = { F: 2, A: 1, N: 0 };

/**
 * One checkpoint in one year. `points` is null for X; for "all locations" it
 * is the average over the locations that rated the checkpoint as relevant.
 * `source` is the assessed status, used for colours and labels so they match
 * the single analysis.
 */
export interface TrendValue {
  status: TrendStatus;
  points: number | null;
  source: SystemStatus;
}

export type Trend = "improved" | "worse" | "unchanged";

export const NOT_RELEVANT: TrendValue = { status: "X", points: null, source: "unknown" };

const TREND_STATUS: Record<SystemStatus, TrendStatus> = {
  fulfilled: "F",
  almost_fulfilled: "A",
  partially_fulfilled: "A",
  only_limited: "N",
  not_fulfilled: "N",
  zero: "X",
  insufficient_information: "X",
  unknown: "X",
};

export function toTrendStatus(status: SystemStatus): TrendStatus {
  return TREND_STATUS[status] ?? "X";
}

export function toTrendValue(status: SystemStatus): TrendValue {
  const trendStatus = toTrendStatus(status);
  return {
    status: trendStatus,
    points: trendStatus === "X" ? null : TREND_POINTS[trendStatus],
    source: status,
  };
}

/** Status shown for an average of several locations. */
const AVERAGE_SOURCE: Record<Exclude<TrendStatus, "X">, SystemStatus> = {
  F: "fulfilled",
  A: "almost_fulfilled",
  N: "not_fulfilled",
};

/** Status closest to an average number of points (0–2). */
export function statusForPoints(points: number): Exclude<TrendStatus, "X"> {
  return points >= 1.5 ? "F" : points >= 0.5 ? "A" : "N";
}

/** Averages the relevant values of several locations; X when none is relevant. */
export function averageTrendValue(values: TrendValue[]): TrendValue {
  const points = values.flatMap((value) => (value.points === null ? [] : [value.points]));
  if (!points.length) return NOT_RELEVANT;
  const average = points.reduce((sum, value) => sum + value, 0) / points.length;
  const status = statusForPoints(average);
  return { status, points: average, source: AVERAGE_SOURCE[status] };
}

/** round(sum(points) / (2 × relevant checkpoints) × 100); null when nothing is relevant. */
export function scoreFor(values: TrendValue[]): number | null {
  const relevant = values.flatMap((value) => (value.points === null ? [] : [value.points]));
  if (!relevant.length) return null;
  const sum = relevant.reduce((total, value) => total + value, 0);
  return Math.round((sum / (2 * relevant.length)) * 100);
}

const EPSILON = 1e-9;

/** Trend of one checkpoint across the years of the period, oldest first. */
export function trendFor(values: TrendValue[]): Trend {
  const last = values.at(-1)?.points ?? null;
  if (last === null) return "unchanged";
  const previous = values.at(-2)?.points ?? null;
  if (previous !== null && last < previous - EPSILON) return "worse";
  const first = values.find((value) => value.points !== null)!.points!;
  if (last > first + EPSILON) return "improved";
  if (last < first - EPSILON) return "worse";
  return "unchanged";
}

/** "all": every year with an analysis; "lastTwo": the two most recent. */
export type PeriodId = "all" | "lastTwo";

export interface PeriodOption {
  id: PeriodId;
  start: number;
  end: number;
  years: number[];
}

/**
 * All years, plus the last two when that is fewer. None with a single year
 * (the first-analysis view).
 */
export function periodOptions(years: number[]): PeriodOption[] {
  const sorted = [...new Set(years)].sort((a, b) => a - b);
  const option = (id: PeriodId, span: number[]): PeriodOption => ({
    id,
    start: span[0]!,
    end: span.at(-1)!,
    years: span,
  });
  if (sorted.length < 2) return [];
  return sorted.length > 2
    ? [option("all", sorted), option("lastTwo", sorted.slice(-2))]
    : [option("all", sorted)];
}

export interface LocationHistory {
  /** Years with an analysis for the location, oldest first. */
  years: number[];
  /** Checkpoint values per year; a checkpoint missing in a year is X. */
  statusByYear: Record<number, Record<string, TrendValue>>;
  analysisByYear: Record<number, SystemAnalysis>;
  /** Elements and checkpoints of the newest year. */
  domains: SystemDomain[];
}

/**
 * Every yearly analysis of one location. For "all locations" each checkpoint
 * gets the average points of the locations assessed that year.
 */
export function buildLocationHistory(
  analyses: SystemAnalysis[],
  locationId: string,
): LocationHistory {
  const isAll = locationId === ALL_LOCATIONS_ID;
  const statusByYear: LocationHistory["statusByYear"] = {};
  const analysisByYear: LocationHistory["analysisByYear"] = {};
  for (const analysis of analyses) {
    const year = Number(analysis.year);
    if (!Number.isFinite(year)) continue;
    const sources = analysis.locations
      .map((location) => location.locationId)
      .filter((id) => id !== ALL_LOCATIONS_ID && (isAll || id === locationId));
    if (!sources.length) continue;
    const valuesBySource = sources.map(
      (source) =>
        new Map(
          (analysis.domainsByLocation[source] ?? [])
            .flatMap((domain) => domain.items)
            .map((item) => [item.id, toTrendValue(item.status)]),
        ),
    );
    const ids = (analysis.domainsByLocation[isAll ? ALL_LOCATIONS_ID : locationId] ?? [])
      .flatMap((domain) => domain.items)
      .map((item) => item.id);
    statusByYear[year] = Object.fromEntries(
      ids.map((id) => {
        const values = valuesBySource.map((source) => source.get(id) ?? NOT_RELEVANT);
        return [id, isAll ? averageTrendValue(values) : values[0]!];
      }),
    );
    analysisByYear[year] = analysis;
  }
  const years = Object.keys(statusByYear)
    .map(Number)
    .sort((a, b) => a - b);
  const latest = years.length ? analysisByYear[years.at(-1)!]! : null;
  const domains = latest
    ? (latest.domainsByLocation[isAll ? ALL_LOCATIONS_ID : locationId] ??
      latest.domainsByLocation[ALL_LOCATIONS_ID] ??
      [])
    : [];
  return { years, statusByYear, analysisByYear, domains };
}
